import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { API, AuthContext } from "../App";
import { toast } from "sonner";
import { usePlanLimits } from "../contexts/PlanLimitContext";
import { useTimezone } from "../contexts/TimezoneContext";

const useSettingsData = () => {
  const { user, setUser } = useContext(AuthContext);
  const { setTimezone: setGlobalTimezone } = useTimezone();
  const { limits, canAddPhoneLine, isNearLimit, refetch: refetchLimits } = usePlanLimits();

  const [loading, setLoading] = useState(false);
  const [showPhoneLimitModal, setShowPhoneLimitModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Business
  const [businessSettings, setBusinessSettings] = useState({
    business_type: "locksmith", business_name: "", business_address: "",
    business_phone: "", business_email: "", tax_id: "", license_number: "", timezone: "America/New_York"
  });

  // SMS
  const [smsSettings, setSmsSettings] = useState({ job_assignment_template: "", available_variables: [] });
  const [smsSettingsLoading, setSmsSettingsLoading] = useState(false);

  // Tech Reminders
  const [techReminderSettings, setTechReminderSettings] = useState({ enabled: false, reminder_time: 60, template: "" });
  const [techReminderLoading, setTechReminderLoading] = useState(false);

  // Customer SMS
  const [customerSmsSettings, setCustomerSmsSettings] = useState({
    job_created_enabled: false, job_scheduled_enabled: false, job_on_the_way_enabled: false,
    job_started_enabled: false, job_completed_enabled: false, job_cancelled_enabled: false,
    quote_sent_enabled: false, invoice_sent_enabled: false, payment_received_enabled: false,
    payment_reminder_enabled: false, appointment_reminder_enabled: false, appointment_reminder_hours: 24,
  });
  const [customerSmsLoading, setCustomerSmsLoading] = useState(false);

  // Job Sources
  const [jobSources, setJobSources] = useState([]);
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [sourceForm, setSourceForm] = useState({
    name: "", type: "referral", commission_type: "percentage", commission_value: 0,
    contact_name: "", contact_phone: "", contact_email: "", notes: "",
    extra_fee_enabled: false, extra_fee_per_job: 0, parts_deduction: "from_total"
  });

  // Job Types
  const [jobTypes, setJobTypes] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [jobTypeDialogOpen, setJobTypeDialogOpen] = useState(false);
  const [editingJobType, setEditingJobType] = useState(null);
  const [jobTypeForm, setJobTypeForm] = useState({ name: "", description: "", category: "", default_duration: 1, hide_parts_in_pdf: false });

  // Phone & Payments
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ name: "", type: "cash", processing_fee: 0, instructions: "", is_default: false, default_collected_by: "company" });

  // Call Masking
  const [callMaskingSettings, setCallMaskingSettings] = useState({ enabled: false, default_caller_id: "", record_calls: false, whisper_message: "Connecting you to {customer_name}", available_numbers: [] });
  const [callMaskingLoading, setCallMaskingLoading] = useState(false);

  // Plans & Billing
  const [availablePlans, setAvailablePlans] = useState([]);
  const [availableDiscounts, setAvailableDiscounts] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [paymentSuccessSession, setPaymentSuccessSession] = useState(null);

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: "", id: "", name: "" });

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  // ─── Init ───────────────────────────────────────────
  useEffect(() => {
    fetchAllSettings();
    fetchPaymentHistory();
    handlePaymentCallback();
  }, []);

  const handlePaymentCallback = async () => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const sessionId = params.get("session_id");

    if (payment === "funds_success" && sessionId) {
      try {
        const res = await axios.get(`${API}/account/add-funds/status/${sessionId}`, getAuthConfig());
        if (res.data.payment_status === "paid") toast.success(`$${res.data.amount?.toFixed(2)} added to your account! Balance: $${res.data.balance?.toFixed(2)}`);
        else toast.info("Payment is being processed.");
      } catch { toast.error("Could not verify payment status."); }
      finally { window.history.replaceState({}, document.title, window.location.pathname); }
    } else if (payment === "funds_cancelled") {
      toast.info("Top-up was cancelled.");
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (payment === "success" && sessionId) {
      // Open the payment confirmation dialog — it polls /payments/status itself
      setPaymentSuccessSession(sessionId);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (payment === "cancelled") {
      toast.info("Payment was cancelled.");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const res = await axios.get(`${API}/payments/history`, getAuthConfig());
      setPaymentHistory(res.data.transactions || []);
    } catch (error) { console.error("Failed to fetch payment history:", error); }
  };

  const fetchAllSettings = async () => {
    setLoading(true);
    try {
      const config = getAuthConfig();
      const [businessRes, sourcesRes, typesRes, phonesRes, paymentsRes, callMaskingRes, plansRes, , smsRes, techReminderRes, customerSmsRes] = await Promise.all([
        axios.get(`${API}/settings/business`, config),
        axios.get(`${API}/settings/job-sources`, config),
        axios.get(`${API}/settings/job-types`, config),
        axios.get(`${API}/settings/phone-numbers`, config),
        axios.get(`${API}/settings/payment-types`, config),
        axios.get(`${API}/call-masking/settings`, config).catch(() => ({ data: null })),
        axios.get(`${API}/plans`, config).catch(() => ({ data: { plans: [] } })),
        axios.get(`${API}/usage/current`, config).catch(() => ({ data: null })),
        axios.get(`${API}/settings/sms`, config).catch(() => ({ data: null })),
        axios.get(`${API}/settings/tech-reminders`, config).catch(() => ({ data: null })),
        axios.get(`${API}/settings/customer-sms`, config).catch(() => ({ data: null })),
      ]);

      setBusinessSettings(businessRes.data);
      setJobSources(sourcesRes.data);
      setJobTypes(typesRes.data);
      setPhoneNumbers(phonesRes.data);
      setPaymentTypes(paymentsRes.data);
      if (callMaskingRes.data) setCallMaskingSettings(callMaskingRes.data);
      if (plansRes.data?.plans) setAvailablePlans(plansRes.data.plans);
      if (plansRes.data?.discounts) setAvailableDiscounts(plansRes.data.discounts);
      if (smsRes.data) setSmsSettings(smsRes.data);
      if (techReminderRes.data) setTechReminderSettings(techReminderRes.data);
      if (customerSmsRes.data) setCustomerSmsSettings(customerSmsRes.data);
      if (businessRes.data.business_type) fetchSuggestions(businessRes.data.business_type);
    } catch (error) { console.error("Failed to fetch settings:", error); }
    finally { setLoading(false); }
  };

  const fetchSuggestions = async (businessType) => {
    try { const res = await axios.get(`${API}/settings/job-types/suggestions/${businessType}`, getAuthConfig()); setSuggestions(res.data); }
    catch (error) { console.error("Failed to fetch suggestions:", error); }
  };

  // ─── Business ───────────────────────────────────────
  const saveBusinessSettings = async () => {
    try {
      const res = await axios.put(`${API}/settings/business`, businessSettings, getAuthConfig());
      setBusinessSettings(res.data);
      if (res.data.business_name) { const u = { ...user, company_name: res.data.business_name }; localStorage.setItem("user", JSON.stringify(u)); setUser(u); }
      if (res.data.timezone) setGlobalTimezone(res.data.timezone);
      fetchSuggestions(businessSettings.business_type);
      toast.success("Business settings saved");
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to save settings"); }
  };

  // ─── SMS ────────────────────────────────────────────
  const saveSmsSettings = async () => {
    setSmsSettingsLoading(true);
    try { const res = await axios.put(`${API}/settings/sms`, { job_assignment_template: smsSettings.job_assignment_template }, getAuthConfig()); setSmsSettings(res.data); toast.success("SMS template saved"); }
    catch { toast.error("Failed to save SMS settings"); }
    finally { setSmsSettingsLoading(false); }
  };

  const resetSmsTemplate = async () => {
    setSmsSettingsLoading(true);
    try { const res = await axios.post(`${API}/settings/sms/reset-template`, {}, getAuthConfig()); setSmsSettings(res.data); toast.success("SMS template reset to default"); }
    catch { toast.error("Failed to reset SMS template"); }
    finally { setSmsSettingsLoading(false); }
  };

  // ─── Tech Reminders ─────────────────────────────────
  const saveTechReminderSettings = async () => {
    setTechReminderLoading(true);
    try { await axios.put(`${API}/settings/tech-reminders`, techReminderSettings, getAuthConfig()); toast.success("Technician reminder settings saved"); }
    catch { toast.error("Failed to save reminder settings"); }
    finally { setTechReminderLoading(false); }
  };

  const resetTechReminderTemplate = () => {
    setTechReminderSettings(prev => ({
      ...prev,
      template: `Hi {{technician_name}},\n\nReminder: You have a job in {{time_until}}\n\n{{customer_name}}\n{{job_address}}\n{{job_city}}, {{job_zip}}\n\n{{job_type}}\n{{job_date}} at {{job_time}}\n\n{{job_notes}}\n\nTap for job details:\n{{assignment_link}}`
    }));
    toast.info("Template reset to default - don't forget to save!");
  };

  // ─── Customer SMS ───────────────────────────────────
  const saveCustomerSmsSettings = async () => {
    setCustomerSmsLoading(true);
    try { const res = await axios.put(`${API}/settings/customer-sms`, customerSmsSettings, getAuthConfig()); setCustomerSmsSettings(res.data); toast.success("Customer SMS settings saved"); }
    catch { toast.error("Failed to save customer SMS settings"); }
    finally { setCustomerSmsLoading(false); }
  };

  // ─── Job Sources ────────────────────────────────────
  const saveJobSource = async () => {
    try {
      if (editingSource) { await axios.put(`${API}/settings/job-sources/${editingSource.source_id}`, sourceForm, getAuthConfig()); toast.success("Job source updated"); }
      else { await axios.post(`${API}/settings/job-sources`, sourceForm, getAuthConfig()); toast.success("Job source created"); }
      setSourceDialogOpen(false); resetSourceForm();
      const res = await axios.get(`${API}/settings/job-sources`, getAuthConfig()); setJobSources(res.data);
    } catch { toast.error("Failed to save job source"); }
  };

  const resetSourceForm = () => { setEditingSource(null); setSourceForm({ name: "", type: "referral", commission_type: "percentage", commission_value: 0, contact_name: "", contact_phone: "", contact_email: "", notes: "", extra_fee_enabled: false, extra_fee_per_job: 0, parts_deduction: "from_total", timezone: "", override_business_name: false }); };

  const editSource = (source) => { setEditingSource(source); setSourceForm({ ...source, extra_fee_enabled: source.extra_fee_enabled || false, extra_fee_per_job: source.extra_fee_per_job || 0, parts_deduction: source.parts_deduction || "from_total", timezone: source.timezone || "", override_business_name: source.override_business_name || false }); setSourceDialogOpen(true); };

  const reorderSources = async (next) => {
    setJobSources(next);
    try {
      const res = await axios.put(`${API}/settings/job-sources/reorder`, { order: next.map((s) => s.source_id) }, getAuthConfig());
      setJobSources(res.data);
    } catch { toast.error("Failed to save source order"); }
  };

  // ─── Job Types ──────────────────────────────────────
  const saveJobType = async () => {
    try {
      if (editingJobType) { await axios.put(`${API}/settings/job-types/${editingJobType.service_id}`, jobTypeForm, getAuthConfig()); toast.success("Service updated"); }
      else { await axios.post(`${API}/settings/job-types`, jobTypeForm, getAuthConfig()); toast.success("Service created"); }
      setJobTypeDialogOpen(false); resetJobTypeForm();
      const res = await axios.get(`${API}/settings/job-types`, getAuthConfig()); setJobTypes(res.data);
    } catch { toast.error("Failed to save service"); }
  };

  const resetJobTypeForm = () => { setEditingJobType(null); setJobTypeForm({ name: "", description: "", category: "", default_duration: 1, hide_parts_in_pdf: false }); };

  const editJobType = (jt) => { setEditingJobType(jt); setJobTypeForm(jt); setJobTypeDialogOpen(true); };

  const importSuggestions = async () => {
    try { await axios.post(`${API}/settings/job-types/bulk`, { services: suggestions }, getAuthConfig()); toast.success(`Imported ${suggestions.length} services`); const res = await axios.get(`${API}/settings/job-types`, getAuthConfig()); setJobTypes(res.data); }
    catch { toast.error("Failed to import services"); }
  };

  // ─── Payment Types ──────────────────────────────────
  const savePaymentType = async () => {
    try {
      if (editingPayment) { await axios.put(`${API}/settings/payment-types/${editingPayment.payment_id}`, paymentForm, getAuthConfig()); toast.success("Payment type updated"); }
      else { await axios.post(`${API}/settings/payment-types`, paymentForm, getAuthConfig()); toast.success("Payment type created"); }
      setPaymentDialogOpen(false); resetPaymentForm();
      const res = await axios.get(`${API}/settings/payment-types`, getAuthConfig()); setPaymentTypes(res.data);
    } catch { toast.error("Failed to save payment type"); }
  };

  const resetPaymentForm = () => { setEditingPayment(null); setPaymentForm({ name: "", type: "cash", processing_fee: 0, instructions: "", is_default: false, default_collected_by: "company" }); };

  const editPayment = (p) => { setEditingPayment(p); setPaymentForm(p); setPaymentDialogOpen(true); };

  const togglePaymentActive = async (paymentId, isActive) => {
    try { await axios.put(`${API}/settings/payment-types/${paymentId}`, { is_active: isActive }, getAuthConfig()); fetchAllSettings(); }
    catch { toast.error("Failed to update payment status"); }
  };

  // ─── Delete ─────────────────────────────────────────
  const handleDelete = async () => {
    const { type, id } = deleteDialog;
    try {
      const endpoints = { source: `/settings/job-sources/${id}`, jobType: `/settings/job-types/${id}`, phone: `/settings/phone-numbers/${id}`, payment: `/settings/payment-types/${id}` };
      await axios.delete(`${API}${endpoints[type]}`, getAuthConfig());
      toast.success("Deleted successfully"); fetchAllSettings();
    } catch { toast.error("Failed to delete"); }
    finally { setDeleteDialog({ open: false, type: "", id: "", name: "" }); }
  };

  const confirmDelete = (type, id, name) => setDeleteDialog({ open: true, type, id, name });

  // ─── Derived ────────────────────────────────────────
  const phoneLimits = limits?.limits?.dedicated_lines || { used: 0, limit: 2, unlimited: false };
  const atPhoneLimit = !canAddPhoneLine();
  const nearPhoneLimit = isNearLimit("dedicated_lines");

  return {
    user, setUser, loading, limits, refetchLimits,
    getAuthConfig, fetchAllSettings,

    // Delete dialog
    deleteDialog, setDeleteDialog, handleDelete, confirmDelete,

    // Business
    businessSettings, setBusinessSettings, saveBusinessSettings,
    showDeleteModal, setShowDeleteModal,

    // Plan & Billing
    availablePlans, availableDiscounts, paymentHistory, billingPeriod, setBillingPeriod,
    paymentSuccessSession, setPaymentSuccessSession, fetchPaymentHistory,

    // Job Sources
    jobSources, sourceDialogOpen, setSourceDialogOpen,
    editingSource, sourceForm, setSourceForm,
    resetSourceForm, editSource, saveJobSource, reorderSources,

    // Job Types
    jobTypes, suggestions, jobTypeDialogOpen, setJobTypeDialogOpen,
    editingJobType, jobTypeForm, setJobTypeForm,
    resetJobTypeForm, editJobType, saveJobType, importSuggestions,

    // Phone
    phoneNumbers, phoneLimits, atPhoneLimit, nearPhoneLimit,
    showPhoneLimitModal, setShowPhoneLimitModal,
    smsSettings, setSmsSettings, smsSettingsLoading, saveSmsSettings, resetSmsTemplate,
    techReminderSettings, setTechReminderSettings, techReminderLoading,
    saveTechReminderSettings, resetTechReminderTemplate,

    // Payments
    paymentTypes, paymentDialogOpen, setPaymentDialogOpen,
    editingPayment, paymentForm, setPaymentForm,
    resetPaymentForm, editPayment, savePaymentType, togglePaymentActive,

    // Call Masking
    callMaskingSettings, setCallMaskingSettings,
    callMaskingLoading, setCallMaskingLoading,

    // Notifications / Customer SMS
    customerSmsSettings, setCustomerSmsSettings,
    customerSmsLoading, saveCustomerSmsSettings,
  };
};

export default useSettingsData;
